import { scopeOwnerProblem } from './closeContract';
import { isValidTime } from './types';
import type { DocketProject, DocketSession, RemainingScopeItem } from './types';

const DAY_MS = 24 * 60 * 60 * 1000;
const SKEW_MS = 5 * 60 * 1000;
const LIVE = new Set(['READY', 'TAKEN', 'WAITS-TIME', 'WAITS-NICK']);

export const readyCount = (sessions: readonly DocketSession[]): number =>
  sessions.filter((s) => s.status === 'READY').length;

export interface OrphanScope { readonly projectId: string; readonly item: string; readonly reason: string }

export function findOrphanScope(
  projects: readonly DocketProject[],
  sessions: readonly DocketSession[],
  nickItemIds?: ReadonlySet<string>,
): OrphanScope[] {
  return projects.flatMap((p) =>
    p.remainingScope.flatMap((item) => {
      const reason = scopeOwnerProblem(item, sessions, p.id, { nickItemIds });
      return reason ? [{ projectId: p.id, item: item.item, reason }] : [];
    }));
}

export interface StaleClaim { readonly session: DocketSession; readonly why: string }

/**
 * Claims that are old, or whose time cannot be trusted (missing, unparseable, in the future):
 * a bad stamp must not hide a stuck claim. An invalid `nowIso` throws: "nothing stale" would be
 * a zero that did not measure.
 */
export function findStaleTaken(sessions: readonly DocketSession[], nowIso: string, maxAgeMs: number = DAY_MS): StaleClaim[] {
  if (!isValidTime(nowIso)) throw new Error(`findStaleTaken: now "${String(nowIso)}" is not a date-time`);
  if (!Number.isFinite(maxAgeMs) || maxAgeMs <= 0) throw new Error(`findStaleTaken: maxAgeMs ${String(maxAgeMs)} is not a positive number`);
  const now = Date.parse(nowIso);
  const out: StaleClaim[] = [];
  for (const s of sessions) {
    if (s.status !== 'TAKEN') continue;
    const at = s.takenBy?.at;
    if (!isValidTime(at)) out.push({ session: s, why: 'no valid claim time' });
    else if (Date.parse(at) > now + SKEW_MS) out.push({ session: s, why: 'claim time is in the future' });
    else if (now - Date.parse(at) > maxAgeMs) out.push({ session: s, why: 'claimed longer ago than the limit' });
  }
  return out;
}

export interface UncoveredProject { readonly project: DocketProject; readonly items: readonly RemainingScopeItem[] }

const liveOwner = (item: RemainingScopeItem, p: DocketProject, sessions: readonly DocketSession[]): boolean => {
  const nickOk = item.nickItemId !== undefined && item.nickItemId.trim() !== '';
  if (item.nickItemId !== undefined && !nickOk) return false;
  if (item.sessionId === undefined) return nickOk;
  const s = sessions.find((x) => x.id === item.sessionId);
  return !!s && s.projectId === p.id && LIVE.has(s.status);
};

/** ACTIVE/STALLED projects where an OPEN scope item has nobody working it, scheduled for it, or waiting on Nick for it. */
export function findUncoveredProjects(projects: readonly DocketProject[], sessions: readonly DocketSession[]): UncoveredProject[] {
  const out: UncoveredProject[] = [];
  for (const p of projects) {
    if (p.status !== 'ACTIVE' && p.status !== 'STALLED') continue;
    const items = p.remainingScope.filter((i) => i.state === 'OPEN' && !liveOwner(i, p, sessions));
    if (items.length > 0) out.push({ project: p, items });
  }
  return out;
}
